import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { LogIn, Pencil, Plus, Trash2 } from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const ACTION_META = {
  login: { label: "Login", icon: LogIn, cls: "bg-blue-50 text-blue-700" },
  create: { label: "Created", icon: Plus, cls: "bg-emerald-50 text-emerald-700" },
  update: { label: "Updated", icon: Pencil, cls: "bg-amber-50 text-amber-700" },
  delete: { label: "Deleted", icon: Trash2, cls: "bg-red-50 text-red-700" },
};

export default function ActivityLog() {
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("all");
  const [range, setRange] = useState({ start: daysAgo(7), end: todayStr() });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/activity", { params: { start: range.start, end: range.end, type } })
      .then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, [range.start, range.end, type]);
  useEffect(() => { load(); }, [load]);

  return (
    <Layout title="Activity Log" subtitle="Who logged in and who changed/added entries (last 90 days)">
      <div className="section-card flex flex-wrap items-center gap-3 mb-5" data-testid="log-filter">
        <span className="text-sm font-semibold text-slate-700">Type:</span>
        <div className="flex gap-1.5">
          {[["all", "All"], ["login", "Logins"], ["change", "Entry Changes"]].map(([v, l]) => (
            <button key={v} data-testid={`log-type-${v}`} onClick={() => setType(v)}
              className={`text-xs font-medium px-3 py-1.5 rounded-lg ${type === v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>{l}</button>
          ))}
        </div>
        <span className="text-sm font-semibold text-slate-700 ml-2">Date:</span>
        <input data-testid="log-start" type="date" max={todayStr()} value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" />
        <span className="text-slate-400 text-xs">→</span>
        <input data-testid="log-end" type="date" max={todayStr()} value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" />
      </div>

      <div className="section-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr><th className="text-left py-3 px-3">When</th><th className="text-left py-3 px-3">User</th><th className="text-left py-3 px-3">Action</th><th className="text-left py-3 px-3">Module</th><th className="text-left py-3 px-3">Details</th></tr>
          </thead>
          <tbody data-testid="log-body">
            {loading && <tr><td colSpan="5" className="text-center py-10 text-slate-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="5" className="text-center py-10 text-slate-400">No activity in this period.</td></tr>}
            {!loading && rows.map((r) => {
              const m = ACTION_META[r.action] || { label: r.action, icon: Pencil, cls: "bg-slate-100 text-slate-700" };
              const Icon = m.icon;
              return (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="py-2 px-3 whitespace-nowrap text-slate-600">{new Date(r.ts).toLocaleString()}</td>
                  <td className="py-2 px-3 font-medium">{r.user_email}</td>
                  <td className="py-2 px-3"><span className={`inline-flex items-center gap-1 stat-pill ${m.cls}`}><Icon size={12} /> {m.label}</span></td>
                  <td className="py-2 px-3 capitalize">{r.module}</td>
                  <td className="py-2 px-3 text-slate-500">{r.summary}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
